import React, { useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppProvider, useApp } from "./contexts/AppContext";
import { useActivity } from "./hooks/useActivity";
import Home from "./pages/Home/Home";
import Screensaver from "./pages/Screensaver/Screensaver";
import ErrorPage from "./pages/ErrorPage/ErrorPage";
import Mobility from "./pages/Mobility/Mobility";
import AdminLogin from "./pages/Admin/AdminLogin";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import ScreensaverManagement from "./pages/Admin/ScreensaverManagement";
import SliderManagement from "./pages/Admin/SliderManagement";
import MarqueeManagement from "./pages/Admin/MarqueeManagement";
import ShopManagement from "./pages/Admin/ShopManagement";
import FloorManagement from "./pages/Admin/FloorManagement";
import TerminalManagement from "./pages/Admin/TerminalManagement";
import RouteManagement from "./pages/Admin/RouteManagement";
import CategoryManagement from "./pages/Admin/CategoryManagement";
import TabManagement from "./pages/Admin/TabManagement";
import EventManagement from "./pages/Admin/EventManagement";
import WorkingHoursManagement from "./pages/Admin/WorkingHoursManagement";
import "./App.css";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

function AppContent() {
  const { state, setCurrentFloor } = useApp();
  const location = useLocation();
  const fetchedTerminalIdRef = useRef<string | null>(null);
  const fetchedTerminalNameRef = useRef<string | null>(null);

  // Read terminalId from URL and set floor accordingly
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const terminalName = params.get("terminalName");
    const terminalId = params.get("terminalId");

    // Preferred: terminalName
    if (terminalName) {
      if (fetchedTerminalNameRef.current === terminalName) return;
      fetchedTerminalNameRef.current = terminalName;
      (async () => {
        try {
          const { apiService } = await import("./services/api");
          const terminals = await apiService.getTerminals({ active: true });
          const match = terminals.find((t) => t.name === terminalName);
          if (match?.floor && Number.isFinite(match.floor)) {
            setCurrentFloor(match.floor);
          }
        } catch (e) {
          // ignore
        }
      })();
      return;
    }

    // Back-compat: terminalId
    if (terminalId) {
      if (fetchedTerminalIdRef.current === terminalId) return;
      fetchedTerminalIdRef.current = terminalId;
      (async () => {
        try {
          const { apiService } = await import("./services/api");
          const terminal = await apiService.getTerminalById(terminalId);
          if (terminal?.floor && Number.isFinite(terminal.floor)) {
            setCurrentFloor(terminal.floor);
          }
        } catch (e) {
          // ignore
        }
      })();
    }
  }, [location.search, setCurrentFloor]);

  // Track user activity
  useActivity();

  // Show error page if backend is unavailable (but not on admin routes)
  const isAdminRoute = location.pathname.startsWith("/admin");
  if (state.isBackendUnavailable && !isAdminRoute) {
    return <ErrorPage />;
  }

  // Render both components but hide inactive one to preserve state
  return (
    <>
      <div style={{ display: state.isScreensaverActive ? "none" : "block" }}>
        <Home />
      </div>
      {state.isScreensaverActive && <Screensaver />}
    </>
  );
}

function AdminPanelClassManager() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) {
      document.body.classList.add("admin-panel");
    } else {
      document.body.classList.remove("admin-panel");
    }

    return () => {
      document.body.classList.remove("admin-panel");
    };
  }, [location.pathname]);

  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppProvider>
        <Router>
          <AdminPanelClassManager />
          <Routes>
            <Route path="/" element={<AppContent />} />
            <Route path="/mobility" element={<Mobility />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route
              path="/admin/screensaver"
              element={<ScreensaverManagement />}
            />
            <Route path="/admin/slider" element={<SliderManagement />} />
            <Route path="/admin/marquee" element={<MarqueeManagement />} />
            <Route path="/admin/shops" element={<ShopManagement />} />
            <Route path="/admin/categories" element={<CategoryManagement />} />
            <Route path="/admin/tabs" element={<TabManagement />} />
            <Route path="/admin/events" element={<EventManagement />} />
            <Route path="/admin/floors" element={<FloorManagement />} />
            <Route path="/admin/terminals" element={<TerminalManagement />} />
            <Route path="/admin/routes" element={<RouteManagement />} />
            <Route
              path="/admin/working-hours"
              element={<WorkingHoursManagement />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AppProvider>
    </QueryClientProvider>
  );
}

export default App;
